import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  updateCreationComponent,
  removeCreationComponent,
} from "../../../store/slices/componentSlice";
import {
  Plus,
  X,
  ChevronDownIcon,
  ChevronUpIcon,
  Pencil,
  Trash2,
} from "lucide-react";
import { renderGrid, getByPath } from "../../common/FormRenderer";
import {
  usePicklistMap,
  resolvePicklistFields,
} from "../../../hooks/usePicklist";
import {
  buildCreateFieldsForPpc,
  PPC_PICKLIST_LABELS,
  recordTypeOptions,
  PPC_MEDIA_UPLOAD_FIELDS,
} from "../../project-page-component/projectPageComponentColumnsConfig";
import { SOBJECT_PROJECT_PAGE_COMPONENT } from "../../../config/sobjectNames";
import ImageLocationFieldInline from "../../project-page-component/ImageLocationFieldInline";
import {
  getPresignedUploadUrl,
  uploadFileToPresignedUrl,
} from "../../../services/backend/s3Api";

const INITIAL_FORM = {
  projectPage: "",
  componentOrder: "",
  description: "",
  recordType: "",
  headline1: "",
  headline2: "",
  headline3: "",
  textArea1: "",
  textArea2: "",
  textArea3: "",
  buttonType: "",
  buttonType2: "",
  buttonType3: "",
  buttonName1: "",
  buttonName2: "",
  buttonName3: "",
  buttonUrl1: "",
  buttonUrl2: "",
  buttonUrl3: "",
  imageLocation1: "",
  imageLocation2: "",
  imageLocation3: "",
  fileUrl1: "",
  fileUrl2: "",
  fileUrl3: "",
};

const ProjectPageComponents = () => {
  const dispatch = useDispatch();
  const components = useSelector(
    (state) => state.component.creation.components,
  );
  const pages = useSelector((state) => state.page.creation.pages);

  const { picklistMap } = usePicklistMap(
    Object.values(PPC_PICKLIST_LABELS),
    SOBJECT_PROJECT_PAGE_COMPONENT,
  );

  const [showModal, setShowModal] = useState(false);
  const [savingComponent, setSavingComponent] = useState(false);
  const [pendingImageFiles, setPendingImageFiles] = useState({
    imageLocation1: null,
    imageLocation2: null,
    imageLocation3: null,
  });
  const objectUrlRefs = useRef({});
  const pendingFilesByComponent = useRef({});
  const [form, setForm] = useState({ ...INITIAL_FORM });
  const [editingId, setEditingId] = useState(null);

  const pageOptions = useMemo(() => {
    if (!Array.isArray(pages)) return [];
    return pages.map((p) => ({
      label: p.projectPageType || p.projectPageName || `Page ${p.id}`,
      value: p.id,
    }));
  }, [pages]);

  const adaptedCreateFields = useMemo(() => {
    const baseFields = buildCreateFieldsForPpc(form);
    return baseFields.map((field) => {
      if (field.cols && field.fields) {
        return {
          ...field,
          fields: field.fields.map((f) =>
            f.path === "projectPage"
              ? {
                  ...f,
                  type: "select",
                  searchType: undefined,
                  options: pageOptions,
                  info: "Select from pages created in Step 4",
                }
              : f,
          ),
        };
      }
      if (field.path === "projectPage") {
        return {
          ...field,
          type: "select",
          searchType: undefined,
          options: pageOptions,
          info: "Select from pages created in Step 4",
        };
      }
      return field;
    });
  }, [pageOptions, form]);

  const handleChange = (path, value) => {
    setForm((prev) => ({ ...prev, [path]: value }));
  };

  const setPendingImageFile = (path, file) => {
    if (objectUrlRefs.current[path]) {
      URL.revokeObjectURL(objectUrlRefs.current[path]);
      objectUrlRefs.current[path] = null;
    }
    if (file) {
      objectUrlRefs.current[path] = URL.createObjectURL(file);
      setForm((prev) => ({ ...prev, [path]: file.name }));
    }
    setPendingImageFiles((s) => ({ ...s, [path]: file }));
  };

  const resetForm = () => {
    setForm({ ...INITIAL_FORM });
    setEditingId(null);
  };

  const openModal = () => {
    resetForm();
    setPendingImageFiles({
      imageLocation1: null,
      imageLocation2: null,
      imageLocation3: null,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleEdit = (component) => {
    setForm({ ...component });
    setEditingId(component.id);
    const savedFiles = pendingFilesByComponent.current[component.id];
    if (savedFiles) {
      setPendingImageFiles({ ...savedFiles });
    } else {
      setPendingImageFiles({
        imageLocation1: null,
        imageLocation2: null,
        imageLocation3: null,
      });
    }
    setShowModal(true);
  };

  const handleRemove = (id) => {
    if (window.confirm("Are you sure you want to remove this component?")) {
      dispatch(removeCreationComponent(id));
      delete pendingFilesByComponent.current[id];
    }
  };

  const getPageLabel = (pageId) => {
    if (!Array.isArray(pages)) return pageId;
    const page = pages.find((p) => String(p.id) === String(pageId));
    return page
      ? page.projectPageType || page.projectPageName || `Page ${page.id}`
      : pageId;
  };

  const saveComponent = async (shouldKeepOpen = false) => {
    const componentId = editingId || Date.now();

    const pathToApiName = {
      imageLocation1: "Image_Location_1__c",
      imageLocation2: "Image_Location_2__c",
      imageLocation3: "Image_Location_3__c",
    };

    const hasPendingUploads = Object.values(pendingImageFiles).some(
      (f) => f && f instanceof File,
    );

    let formWithUploads = { ...form };

    if (hasPendingUploads) {
      setSavingComponent(true);
      try {
        const projectPageId = form.projectPage || "new";
        for (const [path, file] of Object.entries(pendingImageFiles)) {
          if (!file || !(file instanceof File)) continue;
          const apiName = pathToApiName[path];
          if (!apiName) continue;
          const key = `${projectPageId}/${componentId}/${apiName}/${file.name}`;
          const { url } = await getPresignedUploadUrl({
            key,
            contentType: file.type || undefined,
            expiresIn: 900,
          });
          await uploadFileToPresignedUrl(url, file);
          formWithUploads[path] = key;
        }
      } catch (err) {
        console.error("S3 upload failed:", err);
        alert("File upload failed: " + (err?.message || "Unknown error"));
        setSavingComponent(false);
        return;
      }
      setSavingComponent(false);
    }

    if (editingId) {
      const updated = components.map((c) =>
        c.id === editingId ? { ...formWithUploads, id: editingId } : c,
      );
      dispatch(updateCreationComponent(updated));
    } else {
      const newComponent = { ...formWithUploads, id: componentId };
      const updated = Array.isArray(components)
        ? [...components, newComponent]
        : [newComponent];
      dispatch(updateCreationComponent(updated));
    }

    setPendingImageFiles({
      imageLocation1: null,
      imageLocation2: null,
      imageLocation3: null,
    });

    if (!shouldKeepOpen) {
      setShowModal(false);
      resetForm();
    } else {
      resetForm();
    }
  };
  const allContentFields = useMemo(() => {
    return resolvePicklistFields(adaptedCreateFields, picklistMap);
  }, [adaptedCreateFields, picklistMap, form]);

  const imagePaths = new Set([
    "imageLocation1",
    "imageLocation2",
    "imageLocation3",
  ]);

  const gridFields = useMemo(() => {
    return allContentFields.filter((f) => {
      if (f.path && imagePaths.has(f.path)) return false;
      if (f.fields) {
        // For cols-grouped fields, filter out image sub-fields
        const filtered = f.fields.filter((sf) => !imagePaths.has(sf.path));
        return filtered.length > 0;
      }
      return true;
    });
  }, [allContentFields]);

  const visibleMediaFields = useMemo(() => {
    const allFieldPaths = new Set();
    allContentFields.forEach((f) => {
      if (f.path) allFieldPaths.add(f.path);
      if (f.fields)
        f.fields.forEach((sf) => {
          if (sf.path) allFieldPaths.add(sf.path);
        });
    });

    return PPC_MEDIA_UPLOAD_FIELDS.map((mf) => ({
      ...mf,
      path: mf.path.replace("content.", ""),
    })).filter((mf) => allFieldPaths.has(mf.path));
  }, [allContentFields]);

  return (
    <div className="fade-in">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Project Page Components
        </h2>
        <p className="text-gray-600">Add components to your project pages</p>
      </div>

      {Array.isArray(components) && components.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Current Components ({components.length})
          </h3>
          <div className="space-y-3">
            {components.map((comp) => (
              <div
                key={comp.id}
                className="p-4 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between group"
              >
                <div>
                  <div className="font-medium text-gray-900">
                    {comp.recordType || "No Record Type"}
                    {comp.componentOrder && ` — Order #${comp.componentOrder}`}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Page: {getPageLabel(comp.projectPage)}
                    {comp.description && ` | ${comp.description}`}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    type="button"
                    onClick={() => handleEdit(comp)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Edit component"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemove(comp.id)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                    title="Remove component"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={openModal}
        className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors"
        style={{ backgroundColor: "#08979c" }}
        onMouseEnter={(e) => (e.target.style.backgroundColor = "#0ab0b7")}
        onMouseLeave={(e) => (e.target.style.backgroundColor = "#08979c")}
      >
        <Plus className="w-5 h-5" />
        New Component
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingId ? "Edit Component" : "New Project Page Component"}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                <div>
                  {renderGrid(gridFields, {
                    formData: form,
                    onChange: handleChange,
                    showValidation: true,
                  })}
                  <br />
                  {visibleMediaFields.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {visibleMediaFields.map((f) => (
                        <ImageLocationFieldInline
                          key={f.path}
                          label={f.label}
                          path={f.path}
                          value={getByPath(form, f.path)}
                          onChange={handleChange}
                          onPendingFile={setPendingImageFile}
                          pendingFile={pendingImageFiles[f.path]}
                          isCreate={true}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                disabled={savingComponent}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              {!editingId && (
                <button
                  type="button"
                  onClick={() => saveComponent(true)}
                  disabled={savingComponent}
                  className="px-4 py-2"
                  style={{
                    backgroundColor: savingComponent ? "#6b7280" : "#08979c",
                    color: "white",
                    borderRadius: "8px",
                  }}
                  onMouseEnter={(e) => {
                    if (!savingComponent) e.target.style.backgroundColor = "#0ab0b7";
                  }}
                  onMouseLeave={(e) => {
                    if (!savingComponent) e.target.style.backgroundColor = "#08979c";
                  }}
                >
                  {savingComponent ? "Uploading…" : "Save & New"}
                </button>
              )}
              <button
                type="button"
                onClick={() => saveComponent(false)}
                disabled={savingComponent}
                className="px-4 py-2"
                style={{
                  backgroundColor: savingComponent ? "#6b7280" : "#08979c",
                  color: "white",
                  borderRadius: "8px",
                }}
                onMouseEnter={(e) => {
                  if (!savingComponent) e.target.style.backgroundColor = "#0ab0b7";
                }}
                onMouseLeave={(e) => {
                  if (!savingComponent) e.target.style.backgroundColor = "#08979c";
                }}
              >
                {savingComponent ? "Uploading…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectPageComponents;
